import { Component, OnInit, ChangeDetectionStrategy, ChangeDetectorRef, inject } from '@angular/core';
import { Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { TableModule } from 'primeng/table';
import { SelectModule } from 'primeng/select';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { TagModule } from 'primeng/tag';

// Services and models
import { CampaignsService } from '@generated/services/campaigns.service';
import { CampaignDto } from '@generated/models/campaign-dto';
import { CampaignStatus } from '@generated/models/campaign-status';
import { NotificationService } from '@core/services/notification.service';

@Component({
  selector: 'app-outbound-campaigns',
  imports: [
    CommonModule,
    FormsModule,
    ButtonModule,
    TableModule,
    SelectModule,
    ProgressSpinnerModule,
    TagModule
  ],
  templateUrl: './outbound-campaigns.html',
  styleUrl: './outbound-campaigns.css',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class OutboundCampaigns implements OnInit {
  private campaignsService = inject(CampaignsService);
  private notificationService = inject(NotificationService);
  private router = inject(Router);
  private cdr = inject(ChangeDetectorRef);

  // Campaigns data
  campaigns: CampaignDto[] = [];
  isLoading = false;
  nextCursor: string | null = null;
  readonly ROWS_PER_PAGE = 20;

  virtualScrollOptions = {
    lazy: true,
    onLazyLoad: this.onLazyLoad.bind(this),
    itemSize: 60,
    delay: 150,
    showLoader: false,
    loading: false
  };

  // Filters
  selectedStatus: CampaignStatus | null = null;

  // Filter options
  statusOptions: { label: string; value: CampaignStatus }[] = [
    { label: $localize`:@@campaigns.status.initial:Initial`, value: 'Initial' },
    { label: $localize`:@@campaigns.status.processing:Processing`, value: 'Processing' },
    { label: $localize`:@@conversations.status.completed:Completed`, value: 'Completed' }
  ];

  readonly unnamedLabel = $localize`:@@campaigns.unnamed:Unnamed Campaign`;
  readonly unknownStatusLabel = $localize`:@@conversations.status.unknown:Unknown`;

  getStatusLabel(status: CampaignStatus | null | undefined): string {
    if (!status) return this.unknownStatusLabel;
    return this.statusOptions.find(o => o.value === status)?.label ?? status;
  }

  ngOnInit(): void {
    this.loadMoreCampaigns();
  }

  onLazyLoad(event: any): void {
    const last = event.last || 0;
    const currentLength = this.campaigns.length;

    // Предзагрузка: если приближаемся к концу загруженных данных (остается меньше 5 элементов)
    if (currentLength > 0 && last >= currentLength - 5 && this.nextCursor && !this.isLoading) {
      this.loadMoreCampaigns();
    }
  }

  private loadMoreCampaigns(): void {
    if (this.isLoading) return;
    if (this.campaigns.length > 0 && !this.nextCursor) return;

    this.isLoading = true;

    this.campaignsService.apiCampaignsGet({
      Status: this.selectedStatus || undefined,
      Cursor: this.nextCursor || undefined,
      Limit: this.ROWS_PER_PAGE
    }).subscribe({
      next: (result) => {
        const isFirstLoad = this.campaigns.length === 0;

        if (isFirstLoad) {
          this.campaigns = [...(result.campaigns || [])];
        } else {
          this.campaigns.push(...(result.campaigns || []));
        }

        this.nextCursor = result.next_cursor || null;
        this.isLoading = false;
        this.cdr.markForCheck();
      },
      error: (error) => {
        console.error('Failed to load campaigns:', error);
        this.notificationService.showError($localize`:@@campaigns.notify.loadFail:Failed to load campaigns`);
        this.isLoading = false;
        this.cdr.markForCheck();
      }
    });
  }

  onFilterChange(): void {
    this.campaigns = [];
    this.nextCursor = null;
    this.loadMoreCampaigns();
  }

  createNewCampaign() {
    this.router.navigate(['/outbound-campaigns/new']);
  }

  editCampaign(campaign: CampaignDto) {
    this.router.navigate(['/outbound-campaigns', campaign.id]);
  }

  trackByCampaignId(index: number, campaign: CampaignDto): string | number {
    return campaign?.id || index;
  }

  getStatusSeverity(status: CampaignStatus | undefined): 'secondary' | 'info' | 'success' | 'warn' | 'danger' {
    switch (status) {
      case 'Initial':
        return 'secondary';
      case 'Processing':
        return 'info';
      case 'Completed':
        return 'success';
      default:
        return 'secondary';
    }
  }
}
